'use client';

import { Button, Checkbox } from '@common/components';
import { SvgChevrondown } from '@common/icon';
import { cn } from '@core/utils';
import { useState } from 'react';

import { TERM_CATEGORY } from '@/api/types/parent-code';
import { useGlobalStore } from '@/stores/GlobalStore';

interface AgreementSectionProps {
  onNext?: () => void;
  onClose?: () => void;
}

export const AgreementSection = ({
  onNext,
  onClose,
}: AgreementSectionProps) => {
  const [agreements, setAgreements] = useState({
    welloTerms: false,
    privacyCollection: false,
    thirdPartyInfo: false,
    membershipTerms: false,
    addressVerification: false,
    shippingInfo: false,
    voluntaryDonation: false,
    refundPolicy: false,
    giftInfo: false,
  });
  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(false);

  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const handleTermsView = (termType: TERM_CATEGORY) => {
    setTermsDrawerType(termType);
  };

  const requiredAgreements = [
    'welloTerms',
    'privacyCollection',
    'thirdPartyInfo',
    'membershipTerms',
    'addressVerification',
    'shippingInfo',
    'voluntaryDonation',
    'refundPolicy',
    'giftInfo',
  ] as const;

  const thirdPartySubItems = [
    'membershipTerms',
    'addressVerification',
    'shippingInfo',
  ] as const;

  const isAgreementAll = requiredAgreements.every((key) => agreements[key]);

  const isThirdPartyAllChecked = thirdPartySubItems.every(
    (key) => agreements[key],
  );

  const toggleAllAgreements = () => {
    const newValue = !isAgreementAll;
    setAgreements({
      welloTerms: newValue,
      privacyCollection: newValue,
      thirdPartyInfo: newValue,
      membershipTerms: newValue,
      addressVerification: newValue,
      shippingInfo: newValue,
      voluntaryDonation: newValue,
      refundPolicy: newValue,
      giftInfo: newValue,
    });
  };

  const updateAgreement = (key: keyof typeof agreements, checked: boolean) => {
    const newAgreements = {
      ...agreements,
      [key]: checked,
    };

    if (key === 'thirdPartyInfo') {
      thirdPartySubItems.forEach((subKey) => {
        newAgreements[subKey] = checked;
      });
    }
    if (
      thirdPartySubItems.includes(key as (typeof thirdPartySubItems)[number])
    ) {
      const allSubItemsChecked = thirdPartySubItems.every((subKey) =>
        subKey === key ? checked : newAgreements[subKey],
      );
      newAgreements.thirdPartyInfo = allSubItemsChecked;
    }

    setAgreements(newAgreements);
  };

  const toggleThirdPartyAccordion = () => {
    setIsThirdPartyExpanded(!isThirdPartyExpanded);
  };

  return (
    <div className="flex flex-col">
      <p
        className={cn(
          'mb-32 text-start text-Gray-700 typo-body3',
          'mobile-tablet:mb-16 mobile-tablet:typo-body4',
        )}
      >
        이용약관에 동의해주세요.
      </p>

      <div className="mb-16 rounded-xsmall bg-Gray-100 px-12 py-8">
        <Checkbox
          className="font-600"
          checked={isAgreementAll}
          onCheckedChange={toggleAllAgreements}
        >
          전체 동의
        </Checkbox>
      </div>

      <div className="mb-32 flex flex-col">
        <Checkbox
          checked={agreements.welloTerms}
          onCheckedChange={(checked) => updateAgreement('welloTerms', checked)}
          className="justify-between"
        >
          <div className="flex flex-1 items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="font-500 text-Gray-black typo-body4">
                (필수)
              </span>
              <span className="font-400 text-Gray-800 typo-body4">
                웰로 고향사랑기부제 이용약관
              </span>
            </div>
            <button
              className="font-600 underline typo-body4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTermsView(TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS);
              }}
            >
              보기
            </button>
          </div>
        </Checkbox>

        <Checkbox
          checked={agreements.privacyCollection}
          onCheckedChange={(checked) =>
            updateAgreement('privacyCollection', checked)
          }
          className="justify-between"
        >
          <div className="flex flex-1 items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="font-500 text-Gray-black typo-body4">
                (필수)
              </span>
              <span className="font-400 text-Gray-800 typo-body4">
                개인정보 수집이용 동의
              </span>
            </div>
            <button
              className="font-600 underline typo-body4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTermsView(
                  TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT,
                );
              }}
            >
              보기
            </button>
          </div>
        </Checkbox>

        <div className="flex flex-col gap-8">
          <Checkbox
            checked={isThirdPartyAllChecked}
            onCheckedChange={(checked) =>
              updateAgreement('thirdPartyInfo', checked)
            }
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span
                  className="flex cursor-pointer items-start gap-4 font-400 text-Gray-800 typo-body4"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleThirdPartyAccordion();
                  }}
                >
                  <span className="text-start">
                    고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공 동의
                  </span>
                  <SvgChevrondown
                    className={cn(
                      'size-16 min-h-21 min-w-16 transition-transform duration-200',
                      {
                        'rotate-180': isThirdPartyExpanded,
                      },
                    )}
                  />
                </span>
              </div>
            </div>
          </Checkbox>

          {isThirdPartyExpanded && (
            <div className="flex flex-col pl-16">
              <Checkbox
                checked={agreements.membershipTerms}
                onCheckedChange={(checked) =>
                  updateAgreement('membershipTerms', checked)
                }
                className="justify-between"
              >
                <div className="flex flex-1 items-start justify-between gap-4">
                  <span className="font-400 text-Gray-800 typo-body4">
                    고향사랑e음 회원가입을 위한 개인정보 제공 동의
                  </span>
                  <button
                    className="font-600 underline typo-body4"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTermsView(
                        TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT,
                      );
                    }}
                  >
                    보기
                  </button>
                </div>
              </Checkbox>

              <Checkbox
                checked={agreements.addressVerification}
                onCheckedChange={(checked) =>
                  updateAgreement('addressVerification', checked)
                }
                className="justify-between"
              >
                <div className="flex flex-1 items-start justify-between gap-4">
                  <span className="font-400 text-Gray-800 typo-body4">
                    주소지 확인을 위한 주민등록번호 제공 동의
                  </span>
                  <button
                    className="font-600 underline typo-body4"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTermsView(
                        TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT,
                      );
                    }}
                  >
                    보기
                  </button>
                </div>
              </Checkbox>

              <Checkbox
                checked={agreements.shippingInfo}
                onCheckedChange={(checked) =>
                  updateAgreement('shippingInfo', checked)
                }
                className="justify-between"
              >
                <div className="flex flex-1 items-start justify-between gap-4">
                  <span className="font-400 text-Gray-800 typo-body4">
                    답례품 배송 관련 개인정보 제3자 제공 동의
                  </span>
                  <button
                    className="font-600 underline typo-body4"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTermsView(
                        TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT,
                      );
                    }}
                  >
                    보기
                  </button>
                </div>
              </Checkbox>
            </div>
          )}
        </div>

        <Checkbox
          checked={agreements.voluntaryDonation}
          onCheckedChange={(checked) =>
            updateAgreement('voluntaryDonation', checked)
          }
          className="justify-between"
        >
          <div className="flex flex-1 items-start justify-between gap-4">
            <span className="flex gap-4">
              <span className="font-500 text-Gray-black typo-body4">
                (필수)
              </span>
              <span className="font-400 text-Gray-800 typo-body4">
                공무 관련 강요 없이 자발적으로 기부합니다.
              </span>
            </span>
            <button
              className="font-600 underline typo-body4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTermsView(TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE);
              }}
            >
              보기
            </button>
          </div>
        </Checkbox>

        <Checkbox
          checked={agreements.refundPolicy}
          onCheckedChange={(checked) =>
            updateAgreement('refundPolicy', checked)
          }
          className="justify-between"
        >
          <div className="flex flex-1 items-start justify-between gap-4">
            <span className="flex gap-4">
              <span className="font-500 text-Gray-black typo-body4">
                (필수)
              </span>
              <span className="font-400 text-Gray-800 typo-body4">
                안내사항(환불 불가·세액공제 등)을 확인했습니다.
              </span>
            </span>
            <button
              className="font-600 underline typo-body4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTermsView(TERM_CATEGORY.TERMS_GUIDANCE_NOTICE);
              }}
            >
              보기
            </button>
          </div>
        </Checkbox>

        <Checkbox
          checked={agreements.giftInfo}
          onCheckedChange={(checked) => updateAgreement('giftInfo', checked)}
          className="justify-between"
        >
          <div className="flex flex-1 items-start justify-between gap-4">
            <span className="flex gap-4">
              <span className="font-500 text-Gray-black typo-body4">
                (필수)
              </span>
              <span className="font-400 text-Gray-800 typo-body4">
                답례품 정보 확인 및 동의
              </span>
            </span>
            <button
              className="font-600 underline typo-body4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTermsView(TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT);
              }}
            >
              보기
            </button>
          </div>
        </Checkbox>
      </div>
      {isAgreementAll ? (
        <Button
          className="w-full"
          onClick={() => {
            onNext?.();
            onClose?.();
          }}
        >
          기부하기
        </Button>
      ) : (
        <Button
          theme="sub-fill"
          className="w-full"
          onClick={toggleAllAgreements}
        >
          전체 동의하기
        </Button>
      )}
    </div>
  );
};
